import { Modal } from './Modal';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** 確認ボタンの色バリアント */
  variant?: 'danger' | 'success' | 'primary';
  /**
   * 不可逆操作の警告。固定見出し「この操作は取り消すことができません」付きの
   * danger 枠で表示されるため、削除など本当に取り消せない操作にのみ使うこと。
   * 取り消し・再開が可能な操作の影響説明には impactItems を使う
   */
  irreversibleWarning?: string;
  /** 影響を受ける項目のリスト（ノーマルな枠。可逆な操作の影響説明にも使える） */
  impactItems?: string[];
  /** impactItems のラベル（デフォルト: '削除されるデータ:'） */
  impactLabel?: string;
}

export default function ConfirmDialog({
  open,
  title = '確認',
  message,
  confirmText = 'OK',
  cancelText = 'キャンセル',
  onConfirm,
  onCancel,
  variant = 'danger',
  irreversibleWarning,
  impactItems,
  impactLabel = '削除されるデータ:',
}: ConfirmDialogProps) {
  const variantClasses = {
    danger: 'bg-danger-600 hover:bg-danger-700',
    success: 'bg-primary-500 hover:bg-primary-600',
    primary: 'bg-primary-600 hover:bg-primary-700',
  };

  const dotClasses = {
    danger: 'bg-danger-500',
    success: 'bg-primary-500',
    primary: 'bg-primary-500',
  };

  const hasDetailedView = irreversibleWarning || impactItems;

  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      title={title}
      maxWidth="sm"
      showCloseButton={!!hasDetailedView}
      closeOnBackdropClick={true}
    >
      <div className={hasDetailedView ? 'space-y-4' : ''}>
        {/* 不可逆操作の警告セクション */}
        {irreversibleWarning && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-danger-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-danger-800 font-semibold text-sm">
                  この操作は取り消すことができません
                </h3>
                <p className="text-danger-700 text-sm mt-1">{irreversibleWarning}</p>
              </div>
            </div>
          </div>
        )}

        {/* 影響を受ける項目のリスト */}
        {impactItems && impactItems.length > 0 && (
          <div className="bg-text-50 rounded-lg p-4">
            <h4 className="text-text-900 font-medium text-sm mb-2">
              {impactLabel}
            </h4>
            <ul className="space-y-1.5 text-sm text-text-700">
              {impactItems.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${dotClasses[variant]}`} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* メッセージ */}
        {message && (
          <p className={`text-sm text-text-700 ${hasDetailedView ? '' : 'mb-6'}`}>
            {message}
          </p>
        )}

        {/* ボタン */}
        <div className={`flex gap-3 ${hasDetailedView ? 'pt-2' : 'w-full'}`}>
          <button
            type="button"
            className={`flex-1 px-4 py-2 rounded-lg text-white font-medium active:scale-95 transition text-sm sm:text-base ${variantClasses[variant]}`}
            onClick={onConfirm}
            data-autofocus
          >
            {confirmText}
          </button>
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded-lg bg-text-100 text-text-700 font-medium hover:bg-text-200 active:scale-95 transition text-sm sm:text-base"
            onClick={onCancel}
          >
            {cancelText}
          </button>
        </div>
      </div>
    </Modal>
  );
}
